/**
 * fowOverlaySceneManager — SPY-14
 * Single Responsibility: ARAM FoW fog plane + clear vision disks (presentation).
 *
 * Contrato:
 *   getFowFogMaterialProps() → { color, transparent, opacity, depthWrite }
 *   buildFowOverlayGroup(THREE, { width, length }) → { group, fogMesh, clearRoot }
 *   syncFowClearDisks(clearRoot, disks, THREE) → void
 */

import {
  AdditiveBlending,
  CircleGeometry,
  DoubleSide,
  Group,
  Mesh,
  MeshBasicMaterial,
  PlaneGeometry,
  RingGeometry
} from 'three';
import {
  ARAM_FOW_CLEAR_OPACITY,
  ARAM_FOW_FOG_OPACITY,
  ARAM_FOW_Y
} from '../../domain/vision/aramFowOverlay.js';

const FOG_COLOR = 0x0b1220;
const CLEAR_COLOR = 0x64748b;
const RING_COLOR = 0x38bdf8;

export function getFowFogMaterialProps() {
  return {
    color: FOG_COLOR,
    transparent: true,
    opacity: ARAM_FOW_FOG_OPACITY,
    depthWrite: false
  };
}

function resolveClasses(THREE) {
  return {
    GroupCls: THREE?.Group || Group,
    MeshCls: THREE?.Mesh || Mesh,
    PlaneGeo: THREE?.PlaneGeometry || PlaneGeometry,
    CircleGeo: THREE?.CircleGeometry || CircleGeometry,
    RingGeo: THREE?.RingGeometry || RingGeometry,
    MeshMat: THREE?.MeshBasicMaterial || MeshBasicMaterial,
    additive: THREE?.AdditiveBlending ?? AdditiveBlending,
    doubleSide: THREE?.DoubleSide ?? DoubleSide
  };
}

/**
 * Builds a bridge-sized fog plane with a child root for clear disks.
 */
export function buildFowOverlayGroup(THREE, { width, length } = {}) {
  const { GroupCls, MeshCls, PlaneGeo, MeshMat } = resolveClasses(THREE);
  const group = new GroupCls();
  group.name = 'aram-fow-overlay';

  const fogMat = new MeshMat(getFowFogMaterialProps());
  const fogMesh = new MeshCls(new PlaneGeo(width, length), fogMat);
  fogMesh.name = 'aram-fow-fog';
  fogMesh.rotation.x = -Math.PI / 2;
  fogMesh.position.set(0, ARAM_FOW_Y, 0);
  group.add(fogMesh);

  const clearRoot = new GroupCls();
  clearRoot.name = 'aram-fow-clear';
  group.add(clearRoot);

  return { group, fogMesh, clearRoot };
}

/**
 * Rebuilds clear-disk + vision-ring meshes under clearRoot from domain disks.
 */
export function syncFowClearDisks(clearRoot, disks = [], THREE) {
  if (!clearRoot) return;
  if (typeof clearRoot.clear === 'function') {
    clearRoot.clear();
  } else {
    clearRoot.children.length = 0;
  }

  const { GroupCls, MeshCls, CircleGeo, RingGeo, MeshMat, additive, doubleSide } =
    resolveClasses(THREE);

  disks.forEach((disk, i) => {
    const entry = new GroupCls();
    entry.name = `aram-fow-clear-${i}`;
    entry.position.set(disk.x, ARAM_FOW_Y + 0.01, disk.z);

    const fill = new MeshCls(
      new CircleGeo(disk.radius || 1, 32),
      new MeshMat({
        color: CLEAR_COLOR,
        transparent: true,
        opacity: ARAM_FOW_CLEAR_OPACITY,
        depthWrite: false,
        blending: additive
      })
    );
    fill.rotation.x = -Math.PI / 2;
    fill.name = `aram-fow-fill-${i}`;
    entry.add(fill);

    const r = disk.radius || 1;
    const ring = new MeshCls(
      new RingGeo(Math.max(0.05, r - 0.18), r, 48),
      new MeshMat({
        color: RING_COLOR,
        transparent: true,
        opacity: 0.55,
        depthWrite: false,
        side: doubleSide
      })
    );
    ring.rotation.x = -Math.PI / 2;
    ring.name = `aram-fow-ring-${i}`;
    entry.add(ring);

    clearRoot.add(entry);
  });
}
